import { useEffect, useRef, useState } from 'react';
import type { CheckboxOption } from './Checkbox';
import { Ripple } from './Ripple';
import { cn } from './react';
import type { FormControlProps } from './types';

export type ToggleGroupProps<V, O = CheckboxOption<V>> = Omit<
  FormControlProps<V>,
  'required'
> & {
  size: 6 | 8 | 10 | 12;
  options: O[];
  className?: string;
  variant: 'tab' | 'button' | 'chip';
};

export const ToggleGroup = <V = unknown>({
  className,
  value,
  options,
  size,
  onChange,
  autoFocus,
  disabled,
  onBlur,
  onFocus,
  variant,
}: ToggleGroupProps<V>) => {
  const rootEl = useRef<HTMLDivElement>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [maskImage, setMaskImage] = useState<string | undefined>();

  useEffect(() => {
    value;
    const selectedEl = rootEl.current?.querySelector<HTMLButtonElement>(
      '[aria-checked=true]',
    );
    if (!selectedEl || !rootEl.current) return;
    rootEl.current?.scrollTo({
      behavior: 'instant',
      left:
        selectedEl.offsetLeft -
        rootEl.current.offsetWidth / 2 +
        selectedEl.offsetWidth / 2,
    });
  }, [value]);

  useEffect(() => {
    options;
    const el = rootEl.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      const scrolled = Math.abs(el.scrollLeft);
      const canPrev = scrolled > 0;
      const canNext = scrolled < max - 1;
      if (!canPrev && !canNext) {
        setMaskImage(undefined);
        return;
      }
      const isRtl = getComputedStyle(el).direction === 'rtl';
      const fadeLeft = isRtl ? canNext : canPrev;
      const fadeRight = isRtl ? canPrev : canNext;
      setMaskImage(
        `linear-gradient(to right, ${fadeLeft ? 'transparent' : 'black'}, black 0.75rem, black calc(100% - 0.75rem), ${fadeRight ? 'transparent' : 'black'})`,
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    el.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', update);
    };
  }, [options]);

  return (
    <div
      className={cn(
        'flex flex-nowrap items-center whitespace-nowrap overflow-auto scrollbar-none relative',
        variant !== 'chip' && 'border border-border-disabled',
        variant === 'chip' && [
          size === 6 && 'h-6 rounded-md text-xs gap-1',
          size === 8 && 'h-8 rounded-md text-xs gap-2',
          size === 10 && 'h-10 rounded-md text-xs gap-3',
          size === 12 && 'h-12 rounded-lg text-sm gap-3',
        ],
        variant === 'button' && [
          size === 6 && 'h-6 rounded-md text-xs p-px gap-px',
          size === 8 && 'h-8 rounded-md text-xs p-px gap-px',
          size === 10 && 'h-10 rounded-md text-xs p-1 gap-px',
          size === 12 && 'h-12 rounded-lg text-sm p-1 gap-1',
        ],
        variant === 'tab' && [
          size === 6 && 'h-6 text-xs',
          size === 6 && 'h-8 text-xs',
          size === 10 && 'h-10 text-xs',
          size === 12 && 'h-12 text-sm',
          'border-t-0 border-x-0',
        ],
        localLoading && 'shimmer pointer-events-none',
        className,
      )}
      role="radiogroup"
      ref={rootEl}
      style={maskImage ? { maskImage } : undefined}
    >
      {options
        .filter((option) => !option.hidden)
        .map((option, index) => {
          const isChecked = value === option.value;
          return (
            <button
              key={option.key}
              role="radio"
              className={cn(
                'relative overflow-hidden h-full transition-colors duration-400 ease-out shrink-0',
                variant !== 'chip' && 'grow',
                'min-w-min',
                variant === 'chip' && [
                  'rounded-full',
                  'text-content-primary px-2',
                  'border border-border-disabled',
                  'hover:bg-content-primary/5',
                  'aria-checked:bg-background-brand/30 aria-checked:border-background-brand aria-checked:text-content-primary',
                ],
                variant === 'button' && [
                  size === 6 && 'rounded-sm',
                  size === 8 && 'rounded-sm',
                  size === 10 && 'rounded-md',
                  size === 12 && 'rounded-md',
                  'text-content-primary px-3',
                  'hover:bg-content-primary/5',
                  'aria-checked:bg-content-primary aria-checked:text-content-primary-inverse',
                ],
                variant === 'tab' && [
                  'px-widget max-w-min w-full grow',
                  'text-content-tertiary border-b-2 border-b-transparent',
                  'hover:text-content-primary',
                  'aria-checked:text-content-primary aria-checked:border-b-background-brand',
                ],
                'enabled:cursor-pointer',
                'disabled:opacity-50 disabled:pointer-events-none',
                option.className,
              )}
              type="button"
              disabled={option.disabled || disabled || localLoading}
              autoFocus={autoFocus && index === 0}
              onFocus={onFocus}
              onBlur={onBlur}
              aria-checked={isChecked}
              onClick={() => {
                const resp = onChange?.(option.value as never);
                if (resp instanceof Promise) {
                  setLocalLoading(true);
                  resp.finally(() => setLocalLoading(false));
                }
              }}
            >
              {option.label ?? option.key}
              <Ripple />
            </button>
          );
        })}
    </div>
  );
};
